import {
  faCheck,
  faPlus,
  faChevronLeft,
  faChevronRight,
  faShoppingCart,
  faCheckCircle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import useEmblaCarousel from "embla-carousel-react";
import PriceView from "src/Components/GlobalComponents/PriceView";
import { addMultipleItems } from "src/Store/cartSlice";
import "src/assets/css/Carousel/FrequentlyBoughtCarousel.css";
import { CURRENCY } from "src/lib/constans";
import { Skeleton } from "@mui/material";
import { RootState } from "src/Store/store";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { setCartItems } from "src/Store/Cart";

interface IProps {
  data: any[];
  isLoading: boolean;
}

const FrequentlyBoughtCarousels: React.FC<IProps> = ({ data, isLoading }) => {
  const [selectedFrequently, setSelectedFrequently] = useState<any[]>([]);
  const [isAddedAll, setIsAddedAll] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const dispatch = useDispatch();
  const { isLogged } = useSelector((state: RootState) => state.appReducer);
  const { cartItems } = useSelector((state: RootState) => state.cart);
  const [loading, setLoading] = useState(false);
  const [plusSize, setPlusSize] = useState(0);
  const [left, setLeft] = useState(0);

  // Embla Carousel setup
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    loop: false,
    dragFree: true,
  });

  // Update scroll buttons state
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Navigation functions
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // Add selected items to cart
  const handleAddAllToCart = async () => {
    if (selectedFrequently.length === 0 || loading) return;
    setLoading(true);
    // Prepare products array for cart
    const productsToAdd = selectedFrequently.map((index) => {
      const product = data[index];
      return {
        _id: product._id || product.id,
        quantity: 1,
        itemType: "product" as const,
      };
    });

    if (isLogged) {
      const cartResponse: any = await httpRequest({
        path: "/cart/add",
        method: "post",
        params: {
          items: productsToAdd.map((item) => ({
            quantity: 1,
            productId: item._id,
            type: "pharmacy",
          })),
        },
      });
      if (cartResponse.err) {
        console.log(cartResponse.err, "err");
        toast.error(cartResponse.err.message || "Error adding to cart");
        setLoading(false);
        return;
      } else {
        console.log(cartResponse.res, "res");
        const items = cartResponse.res?.cartItems || [];
        dispatch(setCartItems([...items]));
      }
    } else {
      dispatch(
        addMultipleItems({
          category: "pharmacyProducts",
          products: productsToAdd,
        })
      );
    }
    setLoading(false);
    // Show success message
    setIsAddedAll(true);

    // Reset after 3 seconds
    setTimeout(() => {
      setIsAddedAll(false);
      setSelectedFrequently([]);
    }, 3000);
  };

  useEffect(() => {
    const windowResize = (e: any) => {
      let ele: HTMLDivElement | null = document.querySelector(
        ".frequently-bought-embla__container"
      );
      if (ele) {
        let gap = window.getComputedStyle(ele).gap;
        let gapValue = parseFloat(gap);
        let plusSize = gapValue / 2;
        let left = plusSize / 1.5;
        if (window.innerWidth <= 1536 && window.innerWidth >= 1280) {
          // setPlusSize(plusSize * 1.2);
          setPlusSize(plusSize);
          setLeft(left * 1.2);
        } else if (window.innerWidth <= 1280 && window.innerWidth >= 1024) {
          setPlusSize(plusSize);
          setLeft(left * 1.3);
        } else if (window.innerWidth <= 1024) {
          setPlusSize(plusSize);
          setLeft(left * 1.5);
        } else {
          setPlusSize(plusSize);
          setLeft(left);
        }
      }
    };

    setTimeout(() => {
      windowResize(null);
    }, 1000);
    
    window.onload = (e: any) => {
      windowResize(e);
    };
    window.onresize = windowResize;
    return () => {
      window.onresize = null;
      window.onload = null;
    };
  }, [data]);

  return data?.length > 0 ? (
    <div className="box-design p-4">
      {!isLoading && (
        <h2 className="text-[#2D2D2D] font-Poppins-SemiBold font-size-20px mb-4">
          Frequently Bought Together
        </h2>
      )}

      {!isAddedAll ? (
        <div className="relative">
          {/* Navigation Buttons - Only show when scrolling is possible */}
          {canScrollPrev && (
            <button
              className="frequently-bought-embla__button frequently-bought-embla__button--prev"
              onClick={scrollPrev}
            >
              <FontAwesomeIcon icon={faChevronLeft} size="sm" />
            </button>
          )}

          {canScrollNext && (
            <button
              className="frequently-bought-embla__button frequently-bought-embla__button--next"
              onClick={scrollNext}
            >
              <FontAwesomeIcon icon={faChevronRight} size="sm" />
            </button>
          )}

          {/* Carousel Container */}
          <div className="frequently-bought-embla">
            <div className="frequently-bought-embla__viewport" ref={emblaRef}>
              <div className="frequently-bought-embla__container">
                {isLoading
                  ? Array.from({ length: 3 }).map((item, index) => (
                      <div
                        key={index}
                        className="frequently-bought-embla__slide"
                      >
                        <Skeleton
                          variant="rectangular"
                          width="97%"
                          height="100%"
                          className="w-full rounded-md xl:rounded-lg aspect-[1/1.08]"
                        />
                      </div>
                    ))
                  : data?.map((item: any, i: number) => (
                      <div
                        key={i}
                        className="frequently-bought-embla__slide relative"
                      >
                        <div className="w-full h-full flex items-center">
                          <div className="frequently-bought-card w-full relative rounded-[15px] overflow-hidden shadow-[0px_3px_6px_#00000029] border border-[#619B97]">
                            <button
                              disabled={
                                loading ||
                                cartItems.find(
                                  (v: any) => v.productId === item._id
                                )
                                  ? true
                                  : false
                              }
                              title={
                                loading ||
                                cartItems.find(
                                  (v: any) => v.productId === item._id
                                )
                                  ? "Item already in cart"
                                  : "Select Item"
                              }
                              className={`w-[15px] h-[15px] 2xl:w-[20px] 2xl:h-[20px] absolute z-10 top-2 left-2 border-[#00443F] ${
                                !selectedFrequently.includes(i) && "border"
                              }  rounded-full bg-white cursor-pointer ${
                                loading ||
                                cartItems.find(
                                  (v: any) => v.productId === item._id
                                )
                                  ? "opacity-50 cursor-not-allowed"
                                  : "cursor-pointer"
                              }`}
                              onClick={() => {
                                setSelectedFrequently((prev: any[]) => {
                                  if (prev?.includes(i)) {
                                    return prev?.filter(
                                      (ite: number) => ite !== i
                                    );
                                  } else {
                                    return [...prev, i];
                                  }
                                });
                              }}
                            >
                              <div
                                className={`w-full h-full bg-[#00443F] text-white rounded-full flex items-center justify-center ${
                                  selectedFrequently.includes(i) ||
                                  cartItems.find(
                                    (v: any) => v.productId === item._id
                                  )
                                    ? "scale-100"
                                    : "scale-0"
                                } transition-all`}
                              >
                                <FontAwesomeIcon icon={faCheck} size="xs" />
                              </div>
                            </button>

                            <div className="">
                              {/* <div className="frequently-bought-card-content"> */}
                              <div className="frequently-bought-card-image w-full aspect-[1/0.85] select-none flex items-center justify-center bg-[#F6F6F6]">
                                <img
                                  src={item?.image}
                                  className="w-[80%] h-[80%] object-contain"
                                  alt="Garden of Life"
                                />
                              </div>

                              <div className="frequently-bought-card-details h-[80px] lg:h-[6vw] p-3 flex flex-col justify-between gap-1">
                                <p className="text-[#3B3B3B] font-Poppins-Regular font-size-14px line-clamp-2 overflow-hidden w-full">
                                  {item?.productName}
                                </p>
                                <div className="">
                                  <PriceView
                                    salePrice={item?.salePrice}
                                    regularPrice={item?.regularPrice}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {i !== data?.length - 1 && (
                          <FontAwesomeIcon
                            icon={faPlus}
                            className="text-[#00443F] absolute top-1/2 -translate-y-1/2"
                            style={{
                              width: `${plusSize}px`,
                              height: `${plusSize}px`,
                              left: `calc(${left}px + 100%)`,
                            }}
                          />
                        )}
                      </div>
                    ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center py-8 px-4">
          <div className="text-center">
            <div className="flex items-center justify-center">
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="text-green-600 text-[60px]"
              />
            </div>
            <h3 className="text-green-700 font-Poppins-SemiBold text-lg mb-2">
              Successfully Added to Cart!
            </h3>
            <p className="text-green-600 font-Poppins-Regular text-sm">
              {selectedFrequently.length} item
              {selectedFrequently.length > 1 ? "s" : ""} added to your shopping
              cart
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-3 mt-4 w-full relative items-center">
        <div className="flex flex-col">
          {!isLoading && (
            <p className="font-size-20px font-Poppins-Medium text-[#202020] pl-1">
              Total Amount
            </p>
          )}
          {!isLoading && (
            <p className="font-size-17px font-Poppins-Regular  text-[#555555] pl-1">
              {CURRENCY}{" "}
              {data
                ?.reduce(
                  (acc: number, item: any, index: number) =>
                    acc +
                    (selectedFrequently.includes(index)
                      ? item?.salePrice
                        ? Number(item?.salePrice)
                        : item?.regularPrice
                        ? Number(item?.regularPrice)
                        : 0
                      : 0),
                  0
                )
                .toFixed(2)}
            </p>
          )}
        </div>
        <div className="flex flex-1">
          {isLoading ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={"2.1vw"}
              className="rounded-full min-h-8"
            />
          ) : (
            <button
              onClick={handleAddAllToCart}
              disabled={selectedFrequently.length === 0 || loading}
              className={`w-full py-2 px-3 rounded-full font-Lexend-Medium font-size-18px flex items-center justify-center gap-2 transition-all duration-300 ${
                selectedFrequently.length > 0
                  ? "border-[#00443F] hover:bg-gray-100 text-[#00443F] border cursor-pointer"
                  : "bg-gray-200 text-gray-400 border border-gray-200 cursor-not-allowed"
              }`}
            >
              <FontAwesomeIcon icon={faShoppingCart} size="sm" />
              <span>Add All to Cart</span>
              {loading && (
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="animate-spin font-size-16px"
                />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  ) : null;
};

export default FrequentlyBoughtCarousels;
